// ============================================================
// CustomersTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Customer list
//   • Add / edit form (inline, not a modal)
//
// SITS TOP-LEVEL FOR NOW. It belongs under Sales, which does not
// exist yet — moving it is one line in the NAV array.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. CustomerForm
//   C. CustomersTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check, Search } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const StatusPill = ({ active }) => (
  <span style={{
    fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '11px',
    background: active ? C.successBg : C.borderSoft,
    color:      active ? C.success   : C.textMuted,
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      background: 'none', border: 'none', cursor: 'pointer',
      padding: 0, fontFamily: 'inherit',
    }}
  >
    <span style={{
      width: '38px', height: '22px', borderRadius: '11px',
      background: checked ? C.brand : C.border,
      position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#ffffff', transition: 'left 0.15s',
      }} />
    </span>
    <span style={{ fontSize: '14px', color: C.text }}>{label}</span>
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

// The day the system went live. An opening balance is what the
// customer owed on that day, so it is the default date for one.
const GO_LIVE_DATE = '2026-10-03';

// YYYY-MM-DD in local time. toISOString() would read a PKT date
// as the previous day, since Postgres dates arrive as midnight PKT.
const toDateInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  if (isNaN(x.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. CUSTOMER FORM
//
// Only the name is required. A customer often gets added
// mid-order with nothing but a name and a phone number.
// ============================================================

const emptyCustomer = {
  id: null,
  name: '',
  contact_person: '', phone: '', email: '', address: '',
  ntn: '', strn: '', payment_type: 'debit', payment_terms_days: 30,
  is_active: true,
  opening_balance: '', opening_date: '',
};

const Req = () => <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>;

const CustomerForm = ({ form, setForm, onSave, onCancel, saving }) => (
  <div style={{ padding: '20px 22px', background: C.rowAlt, borderBottom: `1px solid ${C.borderSoft}` }}>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '16px', marginBottom: '18px',
    }}>
      <Field label={<>Customer name<Req /></>}>
        <TextInput
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Ahmed Store"
        />
      </Field>
      <Field label="Contact person">
        <TextInput
          value={form.contact_person}
          onChange={e => setForm({ ...form, contact_person: e.target.value })}
          placeholder="Optional"
        />
      </Field>
      <Field label={<>Phone<Req /></>}>
        <TextInput
          value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          placeholder="0300-1234567"
        />
      </Field>
      <Field label="Email">
        <TextInput
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          placeholder="Optional"
        />
      </Field>
      <Field label="Address">
        <TextInput
          value={form.address}
          onChange={e => setForm({ ...form, address: e.target.value })}
          placeholder="Optional"
        />
      </Field>
      <Field label="NTN">
        <TextInput
          value={form.ntn}
          onChange={e => setForm({ ...form, ntn: e.target.value })}
          placeholder="Optional"
        />
      </Field>
      <Field label="STRN">
        <TextInput
          value={form.strn}
          onChange={e => setForm({ ...form, strn: e.target.value })}
          placeholder="Optional"
        />
      </Field>

      {/* Payment type: Debit or Credit */}
      <Field label={<>Payment type<Req /></>}>
        <select
          value={form.payment_type}
          onChange={e => {
            const pt = e.target.value;
            setForm({ ...form, payment_type: pt, payment_terms_days: pt === 'debit' ? 0 : (form.payment_terms_days || 30) });
          }}
          style={{
            width: '100%', padding: '9px 12px', fontSize: '14px',
            border: `1px solid ${C.border}`, borderRadius: '6px',
            background: '#fff', color: C.text,
          }}
        >
          <option value="debit">Debit (Immediate)</option>
          <option value="credit">Credit (On Terms)</option>
        </select>
      </Field>

      {/* Days field — only when credit */}
      {form.payment_type === 'credit' && (
        <Field label={<>Credit days<Req /></>}>
          <TextInput
            type="number"
            value={form.payment_terms_days}
            onChange={e => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 0 })}
            placeholder="30"
            min="1"
          />
        </Field>
      )}

      {/* What the customer owed before the system went live. It is a
          receivable, not a sale, so it never reaches the P&L. */}
      <Field label="Opening balance (Rs)">
        <TextInput
          type="number" step="1" min="0"
          value={form.opening_balance}
          onChange={e => setForm({
            ...form,
            opening_balance: e.target.value,
            opening_date: Number(e.target.value) > 0
              ? (form.opening_date || GO_LIVE_DATE)
              : form.opening_date,
          })}
          placeholder="0"
        />
        <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
          What they owed before the system. Leave blank if nothing.
        </div>
      </Field>

      {Number(form.opening_balance) > 0 && (
        <Field label={<>Opening balance date<Req /></>}>
          <TextInput
            type="date"
            value={form.opening_date}
            onChange={e => setForm({ ...form, opening_date: e.target.value })}
          />
        </Field>
      )}

      {form.id && (
        <Field label="Status">
          <div style={{ paddingTop: '6px' }}>
            <Toggle
              checked={form.is_active}
              onChange={() => setForm({ ...form, is_active: !form.is_active })}
              label={form.is_active ? 'Active' : 'Inactive'}
            />
          </div>
        </Field>
      )}
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : 'Save customer'}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </div>
);


// ============================================================
// C. MAIN COMPONENT
// ============================================================

const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(null);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [banner, setBanner]       = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tape/customers`, { headers });
      setCustomers(res.data.data || []);
    } catch {
      notify('error', 'Could not load customers. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => setForm({
    ...emptyCustomer,
    id: row.id,
    name: row.name || '',
    contact_person: row.contact_person || '',
    phone: row.phone || '',
    email: row.email || '',
    address: row.address || '',
    ntn: row.ntn || '',
    strn: row.strn || '',
    payment_type: row.payment_type || 'debit',
    payment_terms_days: row.payment_terms_days ?? 30,
    is_active: row.is_active !== false,
    opening_balance: Number(row.opening_balance) > 0 ? String(Math.round(Number(row.opening_balance))) : '',
    opening_date: toDateInput(row.opening_date),
  });

  const save = async () => {
    const f = form;
    if (!f.name.trim()) return notify('error', 'Customer name is required');
    if (Number(f.opening_balance) < 0) return notify('error', 'Opening balance cannot be negative');
    if (Number(f.opening_balance) > 0 && !f.opening_date) {
      return notify('error', 'Enter the opening balance date');
    }

    setSaving(true);
    try {
      const payload = {
        name: f.name,
        contact_person: f.contact_person,
        phone: f.phone,
        email: f.email,
        address: f.address,
        ntn: f.ntn,
               strn: f.strn,
        payment_type: f.payment_type,
        payment_terms_days: f.payment_terms_days,
        is_active: f.is_active,
        // Blank means none. The server rounds to whole rupees.
        opening_balance: f.opening_balance === '' ? 0 : f.opening_balance,
        opening_date: Number(f.opening_balance) > 0 ? f.opening_date : null,
      };

      if (f.id) {
        await axios.put(`${API}/tape/customers/${f.id}`, payload, { headers });
        notify('success', 'Customer updated');
      } else {
        await axios.post(`${API}/tape/customers`, payload, { headers });
        notify('success', 'Customer added');
      }
      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/customers/${row.id}`, { headers });
      notify('success', 'Customer deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Search runs on the loaded list rather than the server —
  // customer counts stay small enough that a round trip per
  // keystroke would be wasted effort.
  const term = search.trim().toLowerCase();
  const visible = term
    ? customers.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term) ||
        (c.contact_person || '').toLowerCase().includes(term))
    : customers;

 

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
                    ['Customers', customers.length, C.text],
        ].map(([label, value, colour]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: colour, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Customers
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
                            Buyers for invoices, payments and ledger.
            </p>
          </div>

          <div style={{ position: 'relative', minWidth: '200px' }}>
            <Search
              size={15}
              style={{
                position: 'absolute', left: '12px', top: '50%',
                transform: 'translateY(-50%)', color: C.textMuted,
              }}
            />
            <input
              style={{ ...inputStyle, paddingLeft: '34px' }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name or phone…"
            />
          </div>

          {!form && (
            <Btn onClick={() => setForm(emptyCustomer)}>
              <Plus size={16} /> Add customer
            </Btn>
          )}
        </div>

        {form && (
          <CustomerForm
            form={form}
            setForm={setForm}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Address</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Terms</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Opening</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && visible.map(row => {
                
                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      {row.name}
                      {row.contact_person && (
                        <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400' }}>
                          {row.contact_person}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{row.phone || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: '13px', color: C.textSoft }}>
                      {row.address || '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                      {row.payment_type === 'credit' ? `Credit · ${row.payment_terms_days}d` : 'Debit'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: Number(row.opening_balance) > 0 ? C.text : C.textMuted }}>
                      {Number(row.opening_balance) > 0 ? money(row.opening_balance) : '—'}
                    </td>
                    <td style={tdStyle}><StatusPill active={row.is_active} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn title="Edit" onClick={() => openEdit(row)}>
                          <Pencil size={15} />
                        </IconBtn>
                        <IconBtn danger title="Delete" onClick={() => remove(row)}>
                          <Trash2 size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    {term
                      ? 'No customers match that search.'
                      : 'No customers yet. Click "Add customer" to create the first one.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomersTab;